import type { StrategyTrackResponseDTO } from './types/StrategyTrackResponseDTO';
import type { StrategyPriceResponseDTO } from './types/StrategyPriceResponseDTO';
import request, { checkBlockPopup } from '@/api-v2/request';
import type { PeriodAbbrevEnum } from '@/constants/PeriodAbbrevEnum';
import type { ReturnInterval } from '@/constants/ReturnInterval';
import type { IFrontierLineQuery, IFrontierLineResponse } from '@/types/query/IFrontierLineQuery';
import type { FileSignedUrlDTO } from '@/types/dto/FileSignedUrlDTO';
import { FileService } from '@/api-v2/web/file';
import type { IRollingTrackResponse } from '@/types/IRollingTrackResponse';
import type { IStrategyCorrelationChartQuery } from '@/types/IStrategyCorrelationQuery';
import type { IBenchmarkCorrelationResponseDatum } from '@/types/IBenchmarkCorrelationResponseDatum';
import type { IPureFactorResponse } from './types/IPureFactorResponse';
import type { IRollingCorrelationQuery, IRollingGenericMetricQuery } from '@/types/IRollingTrackQuery';
import type { ITrackQuery } from '@/types/ITrackQuery';
import { RouteName } from '@/constants/RouteName';
import type { CurrencyConversion } from '@/types/CurrencyConversion';
import { useDefaultPdfOptions } from '@/composables/usePdf';
import type { UploadTrackRequestDTO, UploadTrackResponseDTO } from './types/UploadTrackDTO';
import type { StrategyFactsheetResponseDTO } from './types/StrategyFactsheetResponseDTO';
import type { StrategyAttributesDTO } from './types/StrategyAttributesDTO';
import type { StrategyProviderRequestDTO, StrategyProviderResponseDTO } from './types/StrategyProvider';
import type { StrategyIdentifiersRequestDTO, StrategyIdentifiersResponseDTO } from './types/StrategyIdentifiers';
import type { StrategyAttachmentResponseDTO } from './types/StrategyAttachment';
import type { ReturnRegressionStrategyRequestDTO } from '../active-return/types/ReturnRegressionStrategyRequestDTO';
import type { IReturnRegressionResponse } from '@/types/IReturnRegressionResponse';
import type { StrategyAttachmentType } from '@/types/StrategyAttachment';
import type { GetTracksDTO } from './types/GetTracksDTO';
import type { FxConvertedTrackMap } from './types/GetTracksResponseDTO';
import type { IKdeResponseDto } from '@/types/IKdeResponseDto';
import type { IStrategyKdeQuery } from '@/types/IStrategyKdeQuery';
import type { StrategyRawTracksDTO } from './types/StrategyRawTracksDTO';
import type { LiveObjectAccessRightDTO } from '@/types/LiveObjectAccessRightDTO';
import type { DrawdownChartQuery } from '@/types/DrawdownChartQuery';
import type { StrategyFundamentalResponseDTO } from './types/StrategyFundamentalResponseDTO';
import type { RollingZScoreResponseDTO } from '@/types/RollingZScoreResponseDTO';
import type { StrategyTrackProviderResponseDTO } from './types/StrategyTrackProviderResponseDTO';
import type { RollingZScoreQuery } from '@/types/RollingZScoreQuery';

export const getStrategyProviders = (
  params: StrategyProviderRequestDTO,
  signal: AbortSignal,
): Promise<StrategyProviderResponseDTO[]> =>
  request({
    url: `/strategies/providers`,
    method: 'get',
    params,
    signal,
  }).then((o): StrategyProviderResponseDTO[] => o.data);

export const getStrategyIdentifiers = (
  params: StrategyIdentifiersRequestDTO,
  signal: AbortSignal,
): Promise<StrategyIdentifiersResponseDTO[]> =>
  request({
    url: `/strategies/identifiers`,
    method: 'get',
    params,
    signal,
  }).then((o): StrategyIdentifiersResponseDTO[] => o.data);

export const getStrategyFactsheet = (code: string, signal: AbortSignal): Promise<StrategyFactsheetResponseDTO> =>
  request({
    url: `/strategies/${code}/factsheet`,
    method: 'get',
    signal,
  }).then((o): StrategyFactsheetResponseDTO => o.data);

export const setStrategyAttributes = (code: string, data: StrategyAttributesDTO): Promise<void> =>
  request({
    url: `/strategies/${code}/attributes`,
    method: 'post',
    data,
  }).then((o) => o.data);

export const getStrategyDrawdown = (
  code: string,
  query: DrawdownChartQuery,
  signal: AbortSignal,
): Promise<StrategyTrackResponseDTO> =>
  request({
    url: `/strategies/${code}/track`,
    method: 'get',
    params: query,
    signal,
  }).then((o): StrategyTrackResponseDTO => o.data);

export const getStrategyTrack = (
  code: string,
  query: ITrackQuery | undefined,
  signal: AbortSignal,
): Promise<StrategyTrackResponseDTO> =>
  request({
    url: `/strategies/${code}/track`,
    method: 'get',
    params: query,
    signal,
  }).then((o): StrategyTrackResponseDTO => o.data);

/**
 * Fetches selectable track providers for a structured product's track record.
 *
 * @remarks
 * - Only supported for `STRUCTURED_PRODUCT` entities; other entity types return HTTP 400.
 * - `code` is an i18n key; translate it in the frontend and do not show it as-is.
 * - Pass the option's `valueType` to {@link getStrategyPrice}. Omit `valueType` when it is `null` (official / default).
 *
 * @param code - Strategy code.
 * @param signal - Optional abort signal.
 * @returns Track providers available for that product.
 */
export const getStrategyTrackProviders = (
  code: string,
  signal?: AbortSignal,
): Promise<StrategyTrackProviderResponseDTO[]> =>
  request({
    url: `/strategies/${code}/track-providers`,
    method: 'get',
    signal,
  }).then((o): StrategyTrackProviderResponseDTO[] => o.data);

/**
 * Function to get the price or total return track for a strategy code
 * @param code - The strategy code
 * @param query - The query parameters for the request, which can include startDate, endDate, returnInterval, and valueType
 * return interval can be daily, monthly, or yearly, which indicate which re-sampling logic to use, and api side would pick the date and price according to return interval.
 */
export const getStrategyPrice = (
  code: string,
  query: StrategyRawTracksDTO,
  signal: AbortSignal,
): Promise<StrategyPriceResponseDTO[]> =>
  request({
    url: `/strategies/${code}/price`,
    method: 'get',
    params: query,
    signal,
  }).then((o) => o.data);

export const getPureFactorSnapshotTable = (
  date: string,
  signal: AbortSignal,
): Promise<IPureFactorResponse[] | undefined> =>
  request({
    url: `/strategies/research`,
    method: 'get',
    params: {
      date,
    },
    signal,
  }).then((o) => o.data);

/**
 *
 * @param code code of the desired strategy
 * @param query
 */
export const getStrategyRollingTrack = (
  code: string,
  query: IRollingCorrelationQuery | IRollingGenericMetricQuery,
  signal: AbortSignal,
): Promise<IRollingTrackResponse> =>
  request({
    url: `/strategies/${code}/rolling-track`,
    method: 'get',
    params: query,
    signal,
  }).then((o) => o.data);

export const getStrategyReturnRegression = (
  code: string,
  payload: ReturnRegressionStrategyRequestDTO,
  signal: AbortSignal,
): Promise<IReturnRegressionResponse> =>
  request({
    url: `/strategies/${code}/regression`,
    method: 'post',
    data: payload,
    signal,
  }).then((o) => o.data);

/**
 * Return the correlation of a given strategy with `code` vs benchmarks `listBenchmarks`
 */
export const getStrategyBenchmarkCorrelation = ({
  code,
  params,
  signal,
}: {
  code: string;
  params: IStrategyCorrelationChartQuery;
  signal: AbortSignal;
}): Promise<IBenchmarkCorrelationResponseDatum> =>
  request({
    method: 'get',
    url: `/strategies/${code}/correlation`,
    params,
    signal,
  }).then((o) => o.data);

const defaultPdfOptions = useDefaultPdfOptions();

export const getStrategyPdf = async (
  code: string,
  url: string,
  params: {
    returnInterval: ReturnInterval;
    period: PeriodAbbrevEnum;
    startDate: string;
    endDate: string;
  },
  indexName: string,
): Promise<void> => {
  const dto = await request({
    url: `/strategies/${code}/pdf/factsheet`,
    method: 'post',
    data: {
      url,
      params,
      options: defaultPdfOptions,
    },
  }).then((o): FileSignedUrlDTO => o.data);

  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, 'pdf', {
    tool: RouteName.FACTSHEET,
    indexName,
    date: params.endDate,
  });
};

export const getFrontierLine = (
  query: IFrontierLineQuery,
  signal: AbortSignal,
): Promise<IFrontierLineResponse | undefined> =>
  request({
    url: `/strategies/frontier`,
    method: 'POST',
    data: query,
    signal,
  }).then((res) => res.data);

export const getStrategyExcel = async (
  code: string,
  query: {
    endDate: string;
    startDate: string;
    returnInterval: ReturnInterval;
    regimeId?: string;
  } & CurrencyConversion,
  indexName: string,
): Promise<void> => {
  const strategyExcelDto = await request({
    url: `/strategies/${code}/excel`,
    method: 'POST',
    params: query,
  })
    .then((o) => o.data as FileSignedUrlDTO)
    .catch((err) => {
      throw err;
    });
  if (!strategyExcelDto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(strategyExcelDto.signedURL, 'xlsx', {
    tool: RouteName.FACTSHEET,
    indexName,
  });
};

export const uploadStrategy = (data: UploadTrackRequestDTO): Promise<UploadTrackResponseDTO> =>
  request({
    url: `/strategies/upload`,
    method: 'POST',
    data,
  })
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });

export const getStrategyAttachment = (
  code: string,
  signal: AbortSignal,
  type?: StrategyAttachmentType,
): Promise<StrategyAttachmentResponseDTO | null> =>
  request({
    url: `/strategies/${code}/attachment/${type}`,
    method: 'GET',
    signal,
  }).then((res) => res.data);

export const getFxConvertedPriceByDate = (payload: GetTracksDTO, signal: AbortSignal): Promise<FxConvertedTrackMap> =>
  request({
    url: '/strategies/tracks',
    method: 'POST',
    data: payload,
    signal,
  }).then((res) => res.data);

export const getFxConvertedMarketCapByDate = (
  payload: GetTracksDTO,
  signal: AbortSignal,
): Promise<StrategyFundamentalResponseDTO> =>
  request({
    url: '/strategies/tracks/market-cap',
    method: 'POST',
    data: payload,
    signal,
  }).then((res) => res.data);

export const getFxConvertedLiquidityByDate = (
  payload: GetTracksDTO,
  signal: AbortSignal,
): Promise<StrategyFundamentalResponseDTO> =>
  request({
    url: '/strategies/tracks/turnover-by-value',
    method: 'POST',
    data: payload,
    signal,
  }).then((res) => res.data);

export const getStrategyKDE = (
  code: string,
  query: IStrategyKdeQuery | undefined,
  signal: AbortSignal,
): Promise<IKdeResponseDto> =>
  request({
    url: `/strategies/${code}/kde`,
    method: 'get',
    params: query,
    signal,
  }).then((o): IKdeResponseDto => o.data);

/**
 * This endpoint returns different track types of a strategy, like dividend_yield, earnings_per_share
 */
export const getStrategyFundamental = (
  payload: GetTracksDTO,
  signal: AbortSignal,
): Promise<StrategyFundamentalResponseDTO> =>
  request({
    url: `/strategies/fundamentals`,
    method: 'POST',
    data: payload,
    signal,
  }).then((res) => res.data);

/**
 * This endpoint returns the list of strategies for which shouldExposePositions = true
 * It contains the property containsOnlyEquities which tells us if the live strategy/portfolio contains only equities
 * This should be used to set the itemUnderAnalysis variables
 */
export const getLiveObjects = (signal: AbortSignal): Promise<LiveObjectAccessRightDTO[]> => {
  return request({
    url: `/strategies/live-objects`,
    method: 'get',
    signal,
  }).then((o): LiveObjectAccessRightDTO[] => o.data);
};

export const getRollingZScore = (
  code: string,
  query: RollingZScoreQuery | undefined,
  signal: AbortSignal,
): Promise<RollingZScoreResponseDTO> =>
  request({
    url: `/strategies/${code}/rolling-z-score`,
    method: 'get',
    params: query,
    signal,
  }).then((o): RollingZScoreResponseDTO => o.data);
